import React from 'react';
import { Service } from '../../shared/hooks/useServiceSelection';
import CompactDateSelection from '../../layouts/centered/components/CompactDateSelection';
import DateSelection from '../../layouts/centered/components/DateSelection';
import SimpleDateSelection from '../../layouts/centered/components/SimpleDateSelection';
import ImmersiveDateTimeDisplay from '../../layouts/immersive/components/ImmersiveDateTimeDisplay';
import { useLayoutContext } from '../../shared/hooks/useLayoutContext';

// Define possible date/time selection view styles
type DateTimeViewStyle = 'standard' | 'simple' | 'compact' | 'calendar';

interface DateTimeNodeData {
    label: string;
    settings: {
      displayStyle: DateTimeViewStyle;
      colors: {
        cardBackground: string;
        cardBorder: string;
        titleText: string;
        subtitleText: string;
        iconColor: string;
        dateBackground: string;
        dateBorder: string;
        dateText: string;
        selectedDateBackground: string;
        selectedDateBorder: string;
        selectedDateText: string;
        timeslotBackground: string;
        timeslotBorder: string;
        timeslotText: string;
        selectedTimeslotBackground: string;
        selectedTimeslotText: string;
        timeGroupText: string;
        summaryBackground: string;
        summaryBorder: string;
        summaryText: string;
        selectionBackground: string;
        selectionIcon: string;
        buttonBackground: string;
        buttonText: string;
        [key: string]: string;
      };
    };
}

interface DateTimeSelectionWrapperProps {
  selectedServices: Service[];
  selectedTimeZone: string;
  selectedLocation: any;
  selectedDate: Date | null;  // Make sure these are properly typed
  setSelectedDate: (date: Date) => void;  // Add this function prop
  selectedTime: string | null;  // Add this prop
  setSelectedTime: (time: string) => void;  // Add this function prop
  onNext: () => void;
  onPrev: () => void;
  nodeData?: DateTimeNodeData;
}

const DateTimeSelectionWrapper: React.FC<DateTimeSelectionWrapperProps> = ({
  selectedServices,
  selectedTimeZone,
  selectedLocation,
  selectedDate,
  setSelectedDate,
  selectedTime,
  setSelectedTime,
  onNext,
  onPrev,
  nodeData
}) => {
  const { layoutType } = useLayoutContext();

  if (layoutType === 'immersive') {
    return (
      <ImmersiveDateTimeDisplay
        selectedServices={selectedServices}
        selectedTimeZone={selectedTimeZone}
        selectedLocation={selectedLocation}
        selectedDate={selectedDate}
        setSelectedDate={setSelectedDate}
        selectedTime={selectedTime}
        setSelectedTime={setSelectedTime}
        onNext={onNext}
        onPrev={onPrev}
      />
    );
  }

  // Extract settings from nodeData or use defaults
  const settings = nodeData?.settings || {
    displayStyle: 'standard',
    colors: {}
  };

  // Extract the display style and colors from settings
  const displayStyle = settings.displayStyle || 'standard';
  const colors = settings.colors || {};

  // Determine which date/time selection component to render based on displayStyle
  switch (displayStyle) {
    case 'calendar':
    case 'standard':
      return (
        <DateSelection
          selectedServices={selectedServices}
          selectedTimeZone={selectedTimeZone}
          selectedLocation={selectedLocation}
          selectedDate={selectedDate}  // Pass selectedDate to child
          //@ts-expect-error
          setSelectedDate={setSelectedDate}  // Pass setSelectedDate to child
          selectedTime={selectedTime}  // Pass selectedTime to child
          setSelectedTime={setSelectedTime}  // Pass setSelectedTime to child
          onNext={onNext}
          onPrev={onPrev}
          nodeData={nodeData}
        />
      );

    case 'simple':
      return (
        <SimpleDateSelection
          selectedServices={selectedServices}
          selectedTimeZone={selectedTimeZone}
          selectedLocation={selectedLocation}
          selectedDate={selectedDate}  // Pass selectedDate to child
          setSelectedDate={setSelectedDate}  // Pass setSelectedDate to child
          selectedTime={selectedTime}  // Pass selectedTime to child
          setSelectedTime={setSelectedTime}  // Pass setSelectedTime to child
          onNext={onNext}
          onPrev={onPrev}
          nodeData={nodeData}
        />
      );

    case 'compact':
      return (
        <CompactDateSelection
          selectedServices={selectedServices}
          selectedTimeZone={selectedTimeZone}
          selectedLocation={selectedLocation}
          selectedDate={selectedDate}  // Pass selectedDate to child
          //@ts-expect-error
          setSelectedDate={setSelectedDate}  // Pass setSelectedDate to child
          selectedTime={selectedTime}  // Pass selectedTime to child
           //@ts-expect-error
          setSelectedTime={setSelectedTime}  // Pass setSelectedTime to child
          onNext={onNext}
          onPrev={onPrev}
          nodeData={nodeData}
        />
      );

    default:
      return (
        <DateSelection
          selectedServices={selectedServices}
          selectedTimeZone={selectedTimeZone}
          selectedLocation={selectedLocation}
          selectedDate={selectedDate}  // Pass selectedDate to child
           //@ts-expect-error
          setSelectedDate={setSelectedDate}  // Pass setSelectedDate to child
          selectedTime={selectedTime}  // Pass selectedTime to child
          setSelectedTime={setSelectedTime}  // Pass setSelectedTime to child
          onNext={onNext}
          onPrev={onPrev}
          nodeData={nodeData}
        />
      );
  }
};

export default DateTimeSelectionWrapper;
